import apiClient from './client'

export interface Tag {
  id: string
  name: string
}

export interface Note {
  id: string
  name: string
  content: string | null
  full_content: string | null
  file_name: string | null
  file_type: string
  source_url: string | null
  is_archived: boolean
  is_starred: boolean
  is_pinned: boolean
  is_home: boolean
  is_thrashed: boolean
  view_mode: string
  tags: Tag[]
  created_time: number
  modified_time: number
  order: number | null
}

export interface NotesResponse {
  data: Note[]
  count: number
  limit: number
  offset: number
}

export interface NotesFilter {
  view_status?: string
  selected_tags?: Tag[]
  search_keyword?: string
}

// Get notes with filtering
export async function getNotes(
  filter: NotesFilter = {},
  orderBy: string = 'created_time',
  orderDir: string = 'desc',
  skip: number = 0,
  limit: number = 50
): Promise<NotesResponse> {
  const response = await apiClient.post<NotesResponse>(
    `/notes/?skip=${skip}&limit=${limit}&order_by=${orderBy}&order_dir=${orderDir}`,
    filter
  )
  return response.data
}

// Get single note by ID
export async function getNote(noteId: string): Promise<Note> {
  const response = await apiClient.get<Note>(`/notes/note/${noteId}`)
  return response.data
}

// Create a new note
export async function createNote(note: Partial<Note>): Promise<Note> {
  const response = await apiClient.post<Note>('/notes/create', note)
  return response.data
}

// Update an existing note
export async function updateNote(note: Partial<Note>): Promise<Note> {
  const response = await apiClient.put<Note>('/notes/update', note)
  return response.data
}

// Delete a note (move to trash)
export async function deleteNote(noteId: string): Promise<void> {
  await apiClient.delete(`/notes/${noteId}`)
}

// Update source URL on a note
export async function updateSourceUrl(noteId: string, sourceUrl: string | null): Promise<void> {
  await apiClient.put('/notes/partial_update_note', {
    note_ids: [noteId],
    note_partial: { source_url: sourceUrl },
  })
}

// Update the display order of home notes
export async function updateNoteOrder(noteIds: string[]): Promise<void> {
  await apiClient.put('/notes/update_order', noteIds)
}

// Upload a PDF file and create a note from it
export async function uploadPdfNote(
  file: File,
  name: string,
  tags: Tag[],
  summaryLanguage?: string,
): Promise<Note> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('name', name)
  formData.append('tags', JSON.stringify(tags.map(t => ({ id: t.id, name: t.name }))))
  formData.append('view_mode', 'view')
  if (summaryLanguage) formData.append('summary_language', summaryLanguage)

  const response = await apiClient.post<Note>('/notes/file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

// Get all tags
export async function getTags(): Promise<Tag[]> {
  const response = await apiClient.get<{ data: Tag[] }>('/tags/')
  return response.data?.data || []
}

export async function importPocket(
  zipFile: File,
  tagNames: string[],
  onProgress?: (pct: number) => void,
): Promise<{ count: number; failed: string[] }> {
  const formData = new FormData()
  formData.append('file', zipFile)
  if (tagNames.length > 0) {
    formData.append('tags', JSON.stringify(tagNames))
  }

  onProgress?.(10)

  const token = localStorage.getItem('access_token')
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  onProgress?.(20)

  const response = await fetch('/api/v1/notes/import-pocket', {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  })

  onProgress?.(80)

  if (!response.ok) {
    const text = await response.text()
    let detail = ''
    try { detail = JSON.parse(text)?.detail ?? text } catch { detail = text }
    throw new Error(detail || response.statusText)
  }

  const result = await response.json()
  onProgress?.(100)
  return result
}
